import { isAxiosError } from 'axios';

import { API_DOMAIN, WALLET } from 'config/constants';
import { api } from 'lib/api';
import { checkStatus } from 'lib/helpers';

import { z } from 'zod';

/**
 * The durable wallet operations surface, as the API projects it for the backoffice.
 *
 * Five endpoints: `GET wallet/operation` lists the unresolved operations of whoever is
 * asking, `GET wallet/operation/:id` reads one of them, and the three POSTs below resolve
 * one by hand. The scope is taken from the session, so an admin gets every operation
 * without naming anyone, and the three POSTs are refused to anybody who is not an admin.
 */

export const zWalletOperationKind = z.enum([
	'order.charge',
	'order.refund',
	'postnet.intention',
	'transfer.cardholder',
	'transfer.cbu',
]);

export type WalletOperationKind = z.infer<typeof zWalletOperationKind>;

/**
 * The kind as it arrives on a record: one of the above, or whatever the API named it.
 *
 * The API adds kinds on its own release schedule — `postnet.intention` arrived that way and
 * turned this whole listing into an error, because one row of a kind the enum did not name
 * failed the parse of the page it came in. A row nobody here can name is still a row support
 * needs to read, so the record keeps the string verbatim and the screen shows it as the API
 * wrote it. The enum above stays closed for what this release chooses to send: the kind
 * filter offers its options, and a kind the endpoint cannot read is refused rather than
 * dropped.
 */
const zWalletOperationRecordKind = z.union([zWalletOperationKind, z.string()]);

export type WalletOperationRecordKind = z.infer<typeof zWalletOperationRecordKind>;

/**
 * The two client-facing states, derived by the API from `reconcile.review`.
 *
 * `checking` is still being polled by the reconciler. `unresolved` is the one this screen
 * exists for: polling stopped and nothing else happens without a person.
 */
export const zWalletOperationStage = z.enum(['checking', 'unresolved']);

export type WalletOperationStage = z.infer<typeof zWalletOperationStage>;

/**
 * Every state the record can be in, as `WalletOperationState` declares them.
 *
 * `resolved` is the one no provider answer produces: a person established what happened and
 * closed the record by hand. It is exactly what the three actions on this screen write, and
 * the backoffice projection is the only one that carries a resolved operation at all — the
 * listing never shows one, and `GET wallet/operation/:id` answers with one the moment the
 * operator resolves it, which is the response this screen re-renders from.
 */
export const zWalletOperationState = z.enum([
	'claimed',
	'prepared',
	'sent',
	'settled',
	'failed',
	'unresolved',
	'resolved',
]);

export type WalletOperationState = z.infer<typeof zWalletOperationState>;

export const zWalletOperationOutcome = z.enum(['approved', 'rejected', 'pending', 'unknown']);

export type WalletOperationOutcome = z.infer<typeof zWalletOperationOutcome>;

/**
 * A field the API may answer as absent, and Mongo may answer as `null`.
 *
 * The record is a free shape written when something went wrong, so a stored `null` is
 * ordinary and must not fail the parse of the whole listing. Both spellings arrive here and
 * leave as `undefined`, so everything downstream keeps one absent value to check.
 */
const tolerant = <T extends z.ZodTypeAny>(schema: T) => schema.nullish().transform((value) => value ?? undefined);

const zWalletOperationProduct = z.object({
	title: z.string(),
	quantity: z.number(),
});

export type WalletOperationProduct = z.infer<typeof zWalletOperationProduct>;

/**
 * The provider block the backoffice scope exposes.
 *
 * `operationId` and `statusId` are deliberately absent: the API never projects them, to
 * any client. What is here is what the manual matching procedure needs — the endpoint, the
 * direction of the money and the minute the first request was sent.
 */
const zWalletOperationProvider = z.object({
	endpoint: z.string(),
	loadType: tolerant(z.string()),
	attempts: z.number(),
	firstSentAt: tolerant(z.string()),
	lastAttemptAt: tolerant(z.string()),
});

export type WalletOperationProvider = z.infer<typeof zWalletOperationProvider>;

const zWalletOperationReconcile = z.object({
	polls: z.number(),
	nextPollAt: tolerant(z.string()),
	review: z.boolean(),
	reason: tolerant(z.string()),
});

export type WalletOperationReconcile = z.infer<typeof zWalletOperationReconcile>;

/**
 * The intent snapshot, as the charge and refund paths write it.
 *
 * Every field is optional and unknown keys are kept: the record is stored as a free shape
 * on the server and a release that adds a field must not turn this response into a parse
 * error on a screen whose whole job is to be readable when something went wrong.
 */
const zWalletOperationIntent = z
	.object({
		total: tolerant(z.number()),
		amountPaid: tolerant(z.number()),
		pointsUsed: tolerant(z.number()),
		generatedPoints: tolerant(z.number()),
		saleFee: tolerant(z.number()),
		saleDiscount: tolerant(z.number()),
		paymentMethod: tolerant(z.string()),
		status: tolerant(z.string()),
		isInAppSale: tolerant(z.boolean()),
		section: tolerant(z.string()),
		tableNumber: tolerant(z.union([z.string(), z.number()])),
		code: tolerant(z.string()),
		orderId: tolerant(z.string()),
		products: z
			.array(
				z.object({
					title: tolerant(z.string()),
					quantity: tolerant(z.number()),
					price: tolerant(z.number()),
				}),
			)
			.nullish(),
	})
	.passthrough();

export type WalletOperationIntent = z.infer<typeof zWalletOperationIntent>;

/**
 * What a person can do to an operation the reconciler gave up on.
 *
 * The three values are also the path segment each one is sent to, which is why they are used
 * verbatim to build the URL instead of being mapped through a table.
 */
export const zWalletOperationResolutionAction = z.enum(['settle', 'resolve', 'recheck']);

export type WalletOperationResolutionAction = z.infer<typeof zWalletOperationResolutionAction>;

/**
 * What came of one of those actions.
 *
 * The first three are the reconciler's own verdicts, because a re-check is that pass run by
 * hand; `resolved` is the one a provider never produces.
 */
export const zWalletOperationResolutionResult = z.enum(['settled', 'failed', 'pending', 'resolved']);

export type WalletOperationResolutionResult = z.infer<typeof zWalletOperationResolutionResult>;

/**
 * One entry of the audit trail: who, when, which action, and why.
 *
 * The reason is a string and never optional, on this side too: an entry with no stated
 * reason cannot be reviewed later, which is the only thing the trail is for.
 */
const zWalletOperationResolution = z.object({
	action: zWalletOperationResolutionAction,
	by: z.string(),
	reason: z.string(),
	at: z.string(),
	result: zWalletOperationResolutionResult,
});

export type WalletOperationResolution = z.infer<typeof zWalletOperationResolution>;

export const zWalletOperation = z.object({
	id: z.string(),
	kind: zWalletOperationRecordKind,
	stage: zWalletOperationStage,
	amount: z.number(),
	products: z.array(zWalletOperationProduct),
	createdAt: tolerant(z.string()),
	state: zWalletOperationState,
	outcome: tolerant(zWalletOperationOutcome),
	actor: z.string(),
	subject: z.string(),
	orderId: tolerant(z.string()),
	intent: tolerant(zWalletOperationIntent),
	provider: tolerant(zWalletOperationProvider),
	reconcile: tolerant(zWalletOperationReconcile),
	// Whether the provider can still be asked about this operation: it is the exact condition
	// `recheck` refuses with `operation.not_pollable`, published as a fact so the button can be
	// disabled before it is pressed. Absent means a release that did not publish it yet, and
	// the old behaviour — offer the action and render the refusal — is the right answer then.
	pollable: z.boolean().default(true),
	// Whether another operator holds the reconciler's lease right now, read when the answer was
	// built. Absent defaults to nobody holding it, for the same reason: the three actions stay
	// offered and `operation.locked` is still handled when the click lands.
	locked: z.boolean().default(false),
	// The API names this field on every backoffice projection and always as an array, so an
	// operation nobody has touched arrives as an empty list. The default is for the release
	// that answered before the field existed, which would otherwise fail the parse.
	resolutions: z.array(zWalletOperationResolution).default([]),
});

export type WalletOperation = z.infer<typeof zWalletOperation>;

/**
 * The states an operation stops in. Nothing happens to it after one of these, by the system
 * or by a person, so it has no stage worth showing: the API still reports one, because the
 * field is always present, but "waiting" on a completed or failed operation is not true.
 */
const closedWalletOperationStates: WalletOperationState[] = ['settled', 'failed', 'resolved'];

/**
 * Whether the operation is still open, which is when its stage, its lock and whether the
 * provider can be asked about it mean anything to an operator.
 *
 * @param operation The operation
 *
 * @returns True until it reaches a state it stops in
 */
export function isWalletOperationOpen(operation: Pick<WalletOperation, 'state'>): boolean {
	return !closedWalletOperationStates.includes(operation.state);
}

/**
 * The page size the API refuses to exceed.
 *
 * `limit` above this is a 400 and not a listing quietly cut down to 100, so the rows per
 * page this screen offers stop here.
 */
export const WALLET_OPERATIONS_MAX_LIMIT = 100;

/** The rows per page this listing may ask for, given the ceiling above. */
export const walletOperationsRowsPerPageOptions = [10, 25, 50, WALLET_OPERATIONS_MAX_LIMIT];

/**
 * What the API may order the listing by.
 *
 * Two fields and no more: the endpoint refuses anything else, because a sort key it does not
 * index is a blocking sort over a collection nobody prunes.
 */
export const zWalletOperationSortField = z.enum(['createdAt', 'amount']);

export type WalletOperationSortField = z.infer<typeof zWalletOperationSortField>;

export const zWalletOperationSortOrder = z.enum(['asc', 'desc']);

export type WalletOperationSortOrder = z.infer<typeof zWalletOperationSortOrder>;

/**
 * Which operations the screen is looking at, which is the only thing the tabs choose.
 *
 * `review` is the queue this screen has always rendered and the one that needs a person, so
 * it stays the default. `closed` is every way an operation stops being open — the reconciler
 * confirmed it, the reconciler rejected it, or somebody closed it by hand — because an
 * operation completed from this screen ends up `settled` and a tab that listed only
 * `resolved` would lose exactly the decisions it exists to show. `all` is the whole record,
 * for when nobody knows yet how the one being asked about ended.
 */
export const walletOperationQueues = ['review', 'checking', 'closed', 'all'] as const;

export type WalletOperationQueue = (typeof walletOperationQueues)[number];

/**
 * What each queue is, in the endpoint's own two words.
 *
 * A `state` of null sends nothing, which is how the endpoint is asked for its own default of
 * `unresolved` — the two open queues are that default told apart by `stage`, which is the
 * field that says whether the reconciler is still working or has given up.
 */
const walletOperationQueueFilters: Record<
	WalletOperationQueue,
	{ state: WalletOperationState[] | 'any' | null; stage?: WalletOperationStage }
> = {
	review: { state: null, stage: 'unresolved' },
	checking: { state: null, stage: 'checking' },
	closed: { state: ['settled', 'failed', 'resolved'] },
	all: { state: 'any' },
};

/**
 * The `state` parameter a queue is sent as.
 *
 * The endpoint reads a comma separated list of states, or the word `any`, and answers
 * `unresolved` when nothing was sent.
 *
 * @param queue Which operations the screen is looking at
 */
function queueStateParam(queue: WalletOperationQueue): string | null {
	const states = walletOperationQueueFilters[queue].state;

	if (!states) return null;

	return states === 'any' ? states : states.join(',');
}

/**
 * What support narrows the listing by, beyond the queue.
 *
 * `subject` and `actor` are matched exactly by the API — they are usernames and never a
 * prefix — and the two instants bound the operation's start. They are sent as ISO-8601
 * instants rather than as days because a day with no offset is read in UTC, and the person
 * asking means a local Saturday.
 */
export type WalletOperationCriteria = {
	kind?: WalletOperationKind;
	subject?: string;
	actor?: string;
	amountMin?: number;
	amountMax?: number;
	from?: string;
	to?: string;
};

export const emptyWalletOperationCriteria: WalletOperationCriteria = {};

/**
 * What the listing is narrowed to, how it is ordered, and which page of it is wanted.
 *
 * `page` is 0-based because that is what MUI's own pagination counts in, and it is what
 * every other table in this backoffice keeps in state.
 */
export type WalletOperationSearch = WalletOperationCriteria & {
	queue: WalletOperationQueue;
	sort: WalletOperationSortField;
	order: WalletOperationSortOrder;
	page: number;
	rowsPerPage: number;
};

/**
 * The metadata the listing reports when a total was asked for.
 *
 * Only `total` and `totalRelation` are read here; the rest is what the API sends, named so
 * the parse does not have to strip it silently.
 */
export const zWalletOperationPagination = z.object({
	limit: z.optional(z.number()),
	skip: z.optional(z.number()),
	page: z.optional(z.number()),
	hasNextPage: z.optional(z.boolean()),
	total: z.optional(z.number()),
	totalRelation: z.optional(z.enum(['eq', 'gte'])),
	totalPages: z.optional(z.number()),
});

const zWalletOperationEnvelope = z.object({
	data: z.array(zWalletOperation),
	pagination: zWalletOperationPagination,
});

/**
 * One page of the listing.
 *
 * `totalRelation` is `gte` when the count stopped at the framework's ceiling, which makes
 * the total a floor rather than a fact. The screen says so instead of printing it as one.
 */
export type WalletOperationsPage = PaginateSource<WalletOperation> & {
	totalRelation: 'eq' | 'gte';
	/**
	 * Whether the listing could not be read at all.
	 *
	 * A refused search — a range the endpoint will not take, a session that expired — is not
	 * an empty page: telling the operator "no hay operaciones" about a question that was never
	 * answered is the one thing this screen must not do.
	 */
	failed: boolean;
};

const failedPage: WalletOperationsPage = { elements: [], total: 0, totalRelation: 'eq', failed: true };

/**
 * One page of the operations the session may read, narrowed, ordered and paged by the API.
 *
 * Every word of the narrowing is sent rather than applied here: the endpoint reads `state`,
 * `stage`, `kind`, `subject`, `actor`, `amountMin`, `amountMax`, `from`, `to`, `sort`,
 * `order`, `limit` and `skip`, and refuses a parameter it cannot read instead of dropping it.
 * Sorting a page in the browser would order the hundred rows that happened to be fetched and
 * call it the order of the collection, which is the bug this replaces. The total is opt-in,
 * and this screen opts in because the table shows a page count.
 *
 * @param search What to narrow to, how to order it, and which page is wanted
 */
export async function fetchWalletOperations(search: WalletOperationSearch): Promise<WalletOperationsPage> {
	const limit = Math.min(search.rowsPerPage, WALLET_OPERATIONS_MAX_LIMIT);
	const state = queueStateParam(search.queue);
	const stage = walletOperationQueueFilters[search.queue].stage;

	try {
		const response = await api.get(`${API_DOMAIN}${WALLET}/operation`, {
			params: {
				...(state ? { state } : {}),
				...(stage ? { stage } : {}),
				...(search.kind ? { kind: search.kind } : {}),
				...(search.subject ? { subject: search.subject } : {}),
				...(search.actor ? { actor: search.actor } : {}),
				...(search.amountMin !== undefined ? { amountMin: search.amountMin } : {}),
				...(search.amountMax !== undefined ? { amountMax: search.amountMax } : {}),
				...(search.from ? { from: search.from } : {}),
				...(search.to ? { to: search.to } : {}),
				sort: search.sort,
				order: search.order,
				limit,
				skip: search.page * limit,
				count: 'exact',
			},
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		const { data, pagination } = zWalletOperationEnvelope.parse(response.data);

		return {
			elements: data,
			total: pagination.total ?? data.length,
			totalRelation: pagination.totalRelation ?? 'eq',
			failed: false,
		};
	} catch (error) {
		console.error(error);
		return failedPage;
	}
}

/**
 * One operation, by its id.
 *
 * @param id The operation id, which is also the reference support is given
 */
export async function fetchWalletOperation(id: string): Promise<WalletOperation | null> {
	try {
		const response = await api.get(`${API_DOMAIN}${WALLET}/operation/${id}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return zWalletOperation.parse(response.data);
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * Why the API refused a manual resolution, as its top-level `code`.
 *
 * The code is the contract and the message beside it is copy, so the screen branches on
 * these and writes its own sentences. The three codes a customer client branches on are not
 * here: none of them can come out of these three endpoints.
 */
export const zWalletOperationRefusalCode = z.enum([
	/** No reason was given, and a resolution with no stated reason is not reviewable. */
	'operation.reason_required',
	/** The reconciler has not given up on it, so there is nothing to resolve. */
	'operation.not_in_review',
	/** Another operator is acting on it right now. */
	'operation.locked',
	/** The request never returned a provider handle, so there is nothing to ask about. */
	'operation.not_pollable',
	/** Nothing knows how to complete this kind of operation. */
	'operation.not_settleable',
]);

export type WalletOperationRefusalCode = z.infer<typeof zWalletOperationRefusalCode>;

/**
 * What a refused action leaves the screen with: the code it branches on, and the message
 * the API sent, which is the only thing there is to show for a refusal nobody anticipated.
 */
export type WalletOperationRefusal = {
	code: WalletOperationRefusalCode | null;
	message: string | null;
};

/**
 * What the three endpoints answer with: what was done, and the record it was done to.
 *
 * The whole operation comes back rather than an acknowledgement, so the screen that asked
 * re-renders from one response — the state the action left it in, and the audit trail with
 * the entry it just wrote at the end of it.
 */
const zWalletOperationResolved = z.object({
	resolution: zWalletOperationResolution,
	operation: zWalletOperation,
});

export type WalletOperationResolved = z.infer<typeof zWalletOperationResolved>;

export type WalletOperationResolutionResponse =
	({ ok: true } & WalletOperationResolved) | ({ ok: false } & WalletOperationRefusal);

/** The error body every Smaug endpoint refuses with. */
const zApiErrorBody = z.object({
	code: z.optional(z.string()),
	message: z.optional(z.string()),
});

/**
 * Reads why an action was refused off whatever the request threw.
 *
 * A refusal this screen has no sentence for still carries the API's own message, and a
 * failure that is not a refusal at all — the network, a 500 — carries neither.
 *
 * @param error Whatever the request threw
 */
function readRefusal(error: unknown): WalletOperationRefusal {
	if (!isAxiosError(error)) {
		return { code: null, message: null };
	}

	const body = zApiErrorBody.safeParse(error.response?.data);

	if (!body.success) {
		return { code: null, message: null };
	}

	const code = zWalletOperationRefusalCode.safeParse(body.data.code);

	return { code: code.success ? code.data : null, message: body.data.message ?? null };
}

/**
 * Applies one of the three manual actions to an operation, with the reason it was applied
 * for.
 *
 * The three endpoints differ only in the action, which is also the path segment, and the
 * reason is required by all of them: the API refuses a resolution that states none, because
 * an entry nobody can review later is not an audit trail.
 *
 * @param id The operation being resolved
 * @param action What is being done to it
 * @param reason Why, as the operator stated it
 */
export async function resolveWalletOperation(
	id: string,
	action: WalletOperationResolutionAction,
	reason: string
): Promise<WalletOperationResolutionResponse> {
	try {
		const response = await api.post(`${API_DOMAIN}${WALLET}/operation/${id}/${action}`, { reason });

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return { ok: true, ...zWalletOperationResolved.parse(response.data) };
	} catch (error) {
		console.error(error);
		return { ok: false, ...readRefusal(error) };
	}
}
